import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  TARGET_APPLIED_ON_OPTIONS,
  TARGET_FROM_RETAILER_TYPES,
  SELL_TYPE_OPTIONS,
  PAYOUT_TYPE_OPTIONS,
  getInitialSlab,
} from "./constants";

export default function ConfigBuilder({
  config,
  onChange,
  onRemove,
  showRemove,
  targetGroups,
  showTargetGroupSelect,
}) {
  const set = (field, value) => onChange({ ...config, [field]: value });
  const setProtection = (field, value) =>
    onChange({ ...config, protection: { ...config.protection, [field]: value } });

  const isPercentTarget = TARGET_FROM_RETAILER_TYPES.includes(config.targetAppliedOn);
  const isPriceProtection = config.payoutType === "PRICE_PROTECTION";
  const isGift = config.payoutType === "GIFT_REWARD_TRIP";
  const isPoints = config.payoutType === "PER_UNIT_POINTS";
  const rangeLabel = isPercentTarget ? "%" : "units";

  const updateSlab = (index, field, value) => {
    const slabs = [...config.slabs];
    slabs[index] = { ...slabs[index], [field]: value };
    onChange({ ...config, slabs });
  };

  const addSlab = () => onChange({ ...config, slabs: [...(config.slabs || []), getInitialSlab()] });
  const removeSlab = (index) =>
    onChange({ ...config, slabs: config.slabs.filter((_, i) => i !== index) });

  return (
    <Box sx={{ p: 2, border: "1px solid #E2E8F0", borderRadius: 1.5, mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>Payout Config</Typography>
        {showRemove && (
          <IconButton size="small" color="error" onClick={onRemove}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={showTargetGroupSelect ? 6 : 12}>
          <TextField
            fullWidth
            size="small"
            label="Config Name"
            value={config.name || ""}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Upgrade Payout Rs.2400/unit"
          />
        </Grid>

        {showTargetGroupSelect && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Target Group</InputLabel>
              <Select
                label="Target Group"
                value={config.targetGroupId || ""}
                onChange={(e) => set("targetGroupId", e.target.value)}
              >
                {targetGroups.map((tg, i) => (
                  <MenuItem key={i} value={tg._localId || tg.name || i}>{tg.name || `Target Group ${i + 1}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Target Applied On</InputLabel>
            <Select
              label="Target Applied On"
              value={config.targetAppliedOn}
              onChange={(e) => set("targetAppliedOn", e.target.value)}
            >
              {TARGET_APPLIED_ON_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Achievement Basis</InputLabel>
            <Select
              label="Achievement Basis"
              value={config.achievementBasis}
              onChange={(e) => set("achievementBasis", e.target.value)}
            >
              {SELL_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Payout Basis</InputLabel>
            <Select
              label="Payout Basis"
              value={config.payoutBasis}
              onChange={(e) => set("payoutBasis", e.target.value)}
            >
              {SELL_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Payout Type</InputLabel>
            <Select
              label="Payout Type"
              value={config.payoutType}
              onChange={(e) => set("payoutType", e.target.value)}
            >
              {PAYOUT_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Period From (optional override)"
            value={config.periodFrom || ""}
            onChange={(e) => set("periodFrom", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Period To (optional override)"
            value={config.periodTo || ""}
            onChange={(e) => set("periodTo", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Help Text (shown to retailers for explainability)"
            value={config.helpText || ""}
            onChange={(e) => set("helpText", e.target.value)}
          />
        </Grid>

        {isPriceProtection && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                required
                label="Claim Window (days)"
                value={config.protection?.claimWindowDays ?? ""}
                onChange={(e) => setProtection("claimWindowDays", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Brand-Stated Claim Value / Unit"
                value={config.protection?.brandStatedClaimValuePerUnit ?? ""}
                onChange={(e) => setProtection("brandStatedClaimValuePerUnit", e.target.value)}
              />
            </Grid>
          </>
        )}
      </Grid>

      {!isPriceProtection && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", color: "text.secondary" }}>
              Slabs {isPercentTarget && "(ranges are percentages, e.g. 100–120)"}
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addSlab}>Add Slab</Button>
          </Box>

          {(config.slabs || []).map((slab, i) => (
            <Grid container spacing={1} key={i} sx={{ mb: 1, alignItems: "center" }}>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={`From (${rangeLabel})`}
                  value={slab.rangeFrom}
                  onChange={(e) => updateSlab(i, "rangeFrom", e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={`To (${rangeLabel})`}
                  value={slab.rangeTo}
                  onChange={(e) => updateSlab(i, "rangeTo", e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Amount"
                  value={slab.amount}
                  onChange={(e) => updateSlab(i, "amount", e.target.value)}
                />
              </Grid>
              {isGift && (
                <Grid item xs={6} sm={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Gift"
                    value={slab.gift || ""}
                    onChange={(e) => updateSlab(i, "gift", e.target.value)}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={isGift ? 2 : 3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Remark"
                  value={slab.remark || ""}
                  onChange={(e) => updateSlab(i, "remark", e.target.value)}
                />
              </Grid>
              {isPoints && (
                <Grid item xs={6} sm={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!slab.isDefaultSlab}
                        onChange={(e) => updateSlab(i, "isDefaultSlab", e.target.checked)}
                      />
                    }
                    label="Default"
                  />
                </Grid>
              )}
              <Grid item xs={6} sm={1}>
                <IconButton size="small" color="error" onClick={() => removeSlab(i)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Box>
      )}
    </Box>
  );
}
